<script setup lang="ts">
import { computed } from 'vue'
import SourceButton from '~/components/ui/SourceButton.vue'

type SourceEmptyStateSize = 'small' | 'medium' | 'large'

type SourceEmptyStateAction = {
  text: string
  type?: 'primary' | 'secondary' | 'tertiary' | 'deletePrimary' | 'deleteSecondary' | 'deleteTertiary'
  icon?: string
  onClick?: () => void
}

const props = withDefaults(
  defineProps<{
    size?: SourceEmptyStateSize
    icon?: string
    illustration?: string
    illustrationAlt?: string
    title?: string
    description?: string
    actions?: SourceEmptyStateAction[]
  }>(),
  {
    size: 'medium',
    icon: 'inbox',
    illustration: undefined,
    illustrationAlt: undefined,
    title: '',
    description: undefined,
    actions: () => [],
  }
)

const hasMedia = computed(() => Boolean(props.illustration))
</script>

<template>
  <div
    class="source-empty-state"
    :class="[
      `source-empty-state--${size}`,
      { 'source-empty-state--with-media': hasMedia },
    ]"
  >
    <img
      v-if="hasMedia"
      class="source-empty-state__illustration"
      :src="illustration"
      :alt="illustrationAlt ?? ''"
      :aria-hidden="illustrationAlt ? undefined : 'true'"
    >
    <span
      v-else
      class="material-symbols-rounded source-empty-state__icon"
      aria-hidden="true"
    >{{ icon }}</span>

    <div v-if="title || description || $slots.default" class="source-empty-state__body">
      <p v-if="title" class="source-empty-state__title">{{ title }}</p>
      <p v-if="description" class="source-empty-state__description">{{ description }}</p>
      <slot />
    </div>

    <div v-if="actions.length || $slots.actions" class="source-empty-state__actions">
      <slot name="actions">
        <SourceButton
          v-for="(action, actionIndex) in actions"
          :key="`${action.text}-${actionIndex}`"
          :text="action.text"
          :type="action.type ?? 'primary'"
          :icon="action.icon"
          size="small"
          @click="action.onClick?.()"
        />
      </slot>
    </div>
  </div>
</template>

<style scoped lang="scss">
@use "~/assets/scss/partials/elpdl/colors" as c;
@use "~/assets/scss/partials/elpdl/spacing" as *;

.source-empty-state {
  align-items: center;
  display: flex;
  flex-direction: column;
  gap: $spacing-xs;
  justify-content: center;
  padding: $spacing-xl;
  text-align: center;
  width: 100%;
  box-sizing: border-box;
}

.source-empty-state__icon {
  color: c.$enedis-neutral-200;
  font-variation-settings: "FILL" 0, "wght" 400, "GRAD" 0, "opsz" 48;
}

.source-empty-state__illustration {
  display: block;
  height: auto;
  max-width: 100%;
  object-fit: contain;
}

.source-empty-state__body {
  align-items: center;
  display: flex;
  flex-direction: column;
  gap: $spacing-xs;
  max-width: 59rem;
  width: 100%;
}

.source-empty-state__title {
  color: c.$enedis-neutral-900;
  font-family: Public Sans, sans-serif;
  font-size: 1.25rem;
  font-weight: 400;
  line-height: 1.5rem;
  margin: 0;
}

.source-empty-state__description {
  color: c.$enedis-neutral-400;
  font-family: Public Sans, sans-serif;
  font-size: 1rem;
  font-weight: 400;
  line-height: 1.25rem;
  margin: 0;
  max-width: 32rem;
}

.source-empty-state__actions {
  align-items: center;
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: $spacing-xs;
  justify-content: center;
}

.source-empty-state--small {
  padding: $spacing-l;

  .source-empty-state__icon {
    font-size: 2rem;
  }
}

.source-empty-state--medium {
  padding: $spacing-xl;

  .source-empty-state__icon {
    font-size: 3rem;
  }

  &.source-empty-state--with-media .source-empty-state__illustration {
    max-width: 12.5rem;
  }
}

.source-empty-state--large {
  padding: $spacing-2xl;

  .source-empty-state__icon {
    font-size: 4rem;
  }

  &.source-empty-state--with-media .source-empty-state__illustration {
    max-width: 28rem;
  }
}
</style>
